import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { SubscriptionAccessService } from 'src/app/shared/services/subscription-access.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface UpgradeDialogData {
  subscription: any;
  packages: any[];
}

@Component({
  selector: 'app-upgrade-dialog',
  templateUrl: './upgrade-dialog.component.html',
  styleUrls: ['./upgrade-dialog.component.css']
})
export class UpgradeDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  upgradeForm: FormGroup;
  currentSubscription: any;
  loading = false;
  upgradeType: 'immediate' | 'at_renewal' = 'immediate';

  selectedDomains: string[] = [];
  domains = [
    { name: 'Consultancy', price: 1000, unit: 'per project', description: 'Expert-based analysis' },
    { name: 'Road', price: 1.5, unit: 'per km', description: '' },
    { name: 'Construction', price: 1.5, unit: 'per sq.m.', description: 'Shops & malls' },
    { name: 'Utilities', price: 4.5, unit: 'per sq.m.', description: 'Water & electricity' },
  ];

  monthOptions: number[] = Array.from({ length: 22 }, (_, i = 0) => i + 3);

  subtotal = 0;
  gstAmount = 0;
  orderTotal = 0;
  upgradeDetails: any = null;
  newEndDate: Date | null = null;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private subscriptionAccessService: SubscriptionAccessService,
    public dialogRef: MatDialogRef<UpgradeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpgradeDialogData
  ) {
    this.currentSubscription = data.subscription;
    this.initializeForm();
    this.loadCurrentDomains();
  }

  ngOnInit(): void {
    this.upgradeForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateTotal();
      });
    this.calculateTotal();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get amountDue(): number {
    const quoted = Number(this.upgradeDetails?.charge_total);
    if (Number.isFinite(quoted) && quoted >= 0) return quoted;
    return this.orderTotal;
  }

  private usersFromDomain(field: unknown): number {
    let value = field;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch { return 0; }
    }
    if (!value || typeof value !== 'object') return 0;
    const n = Number((value as Record<string, unknown>)['users_count']);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  private loadCurrentDomains(): void {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sub = this.currentSubscription || {};
    const domainNames = ['Road', 'Construction', 'Utilities', 'Consultancy'];
    this.selectedDomains = domainNames.filter((name) => this.usersFromDomain(sub[name.toLowerCase()]) > 0);

    if (!this.selectedDomains.length) {
      const currentDomains = userData.user?.domain?.split(',') || [];
      this.selectedDomains = currentDomains
        .map((d: string) => d.trim())
        .filter((d: string) => this.domains.some((domain) => domain.name === d));
    }

    if (this.currentSubscription) {
      this.upgradeForm.patchValue({
        duration: this.currentSubscription.duration || 3,
        organization_id: userData.user?.branch_id || userData.user?.org_id,
        organization_code: userData.user?.branch_code || userData.user?.company_code,
        Road: { users_count: this.usersFromDomain(sub.road) },
        Construction: { users_count: this.usersFromDomain(sub.construction) },
        Utilities: { users_count: this.usersFromDomain(sub.utilities) },
        Consultancy: { users_count: this.usersFromDomain(sub.consultancy) },
      }, { emitEvent: false });
    }
  }

  private initializeForm(): void {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    this.upgradeForm = this.fb.group({
      organization_id: [userData.user?.branch_id || userData.user?.org_id],
      organization_code: [userData.user?.branch_code || userData.user?.company_code],
      duration: [3, Validators.required],
      total_amount: [0],
      users_count: [0],
      consultancy_projects: [0],
      Road: this.fb.group({ users_count: [0] }),
      Construction: this.fb.group({ users_count: [0] }),
      Utilities: this.fb.group({ users_count: [0] }),
      Consultancy: this.fb.group({ users_count: [0] }),
    });
  }

  onDomainChange(event: any): void {
    const domain = event.source.value;
    const checked = event.checked;

    if (checked) {
      if (!this.selectedDomains.includes(domain)) {
        this.selectedDomains.push(domain);
      }
    } else {
      this.selectedDomains = this.selectedDomains.filter((d) => d !== domain);
      if (this.upgradeForm.get(domain)) {
        this.upgradeForm.get(domain)?.patchValue({ users_count: 0 });
      }
    }
    this.calculateTotal();
  }

  calculateTotal(): void {
    const value = this.upgradeForm.value;
    let total = 0;

    if (this.selectedDomains.includes('Road')) {
      total += (value.Road.users_count * 400);
    }
    if (this.selectedDomains.includes('Construction')) {
      total += (value.Construction.users_count * 400);
    }
    if (this.selectedDomains.includes('Utilities')) {
      total += (value.Utilities.users_count * 400);
    }
    if (this.selectedDomains.includes('Consultancy')) {
      total += (value.Consultancy.users_count * 400);
    }

    this.subtotal = Math.round(total) * (value.duration || 1);
    this.gstAmount = Math.round(this.subtotal * 0.18);
    this.orderTotal = this.subtotal + this.gstAmount;
    this.upgradeForm.patchValue({ total_amount: this.orderTotal }, { emitEvent: false });

    if (this.currentSubscription?.id && this.selectedDomains.length > 0) {
      this.calculateUpgradeCostForDisplay();
    }
  }

  calculateUpgradeCostForDisplay(): void {
    if (!this.currentSubscription?.id) return;
    const formData = this.upgradeForm.value;

    this.apiService.calculateUpgradeCost(this.currentSubscription.id, {
      new_duration: formData.duration,
      upgrade_type: 'immediate',
      Road: formData.Road,
      Construction: formData.Construction,
      Utilities: formData.Utilities,
      Consultancy: formData.Consultancy
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res: any) => {
        if (res.success) {
          this.upgradeDetails = res.data.upgradeDetails;
          if (this.upgradeDetails.new_end_date) {
            this.newEndDate = new Date(this.upgradeDetails.new_end_date);
          }
        }
      },
      error: (err) => {
        console.error('Error calculating upgrade cost:', err);
      }
    });
  }

  proceedToPayment(): void {
    if (this.selectedDomains.length === 0) {
      this.toastrService.warning('Please select at least one domain');
      return;
    }
    if (this.orderTotal === 0) {
      this.toastrService.warning('Please configure users for selected domains');
      return;
    }
    if (!this.currentSubscription?.id) {
      this.toastrService.error('Current subscription not found');
      return;
    }

    this.loading = true;
    this.calculateUpgradeCost().then((upgradeDetails) => {
      if (!upgradeDetails) {
        this.loading = false;
        return;
      }
      const charge = Number(upgradeDetails.charge_total ?? upgradeDetails.upgrade_amount ?? 0);
      if (charge <= 0) {
        this.applyUpgrade();
        return;
      }
      this.openRazorpay(charge, upgradeDetails);
    }).catch(() => {
      this.loading = false;
    });
  }

  calculateUpgradeCost(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.currentSubscription?.id) {
        this.toastrService.error('Current subscription not found');
        reject('No subscription found');
        return;
      }

      const formData = this.upgradeForm.value;
      this.apiService.calculateUpgradeCost(this.currentSubscription.id, {
        new_duration: formData.duration,
        upgrade_type: 'immediate',
        Road: formData.Road,
        Construction: formData.Construction,
        Utilities: formData.Utilities,
        Consultancy: formData.Consultancy
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.upgradeDetails = res.data.upgradeDetails;
            if (this.upgradeDetails.new_end_date) {
              this.newEndDate = new Date(this.upgradeDetails.new_end_date);
            }
            resolve(res.data.upgradeDetails);
          } else {
            this.toastrService.error(res.message || 'Failed to calculate upgrade cost');
            reject(res.message);
          }
        },
        error: (err) => {
          this.toastrService.error(err.error?.message || err.error?.error || 'Failed to calculate upgrade cost');
          reject(err);
        }
      });
    });
  }

  private openRazorpay(amount: number, upgradeDetails: any): void {
    const formData = this.upgradeForm.value;
    this.apiService.createSubscriptionRazorpayOrder({
      amount,
      currency: 'INR',
      purpose: 'upgrade',
      subscription_id: this.currentSubscription.id,
      subscription_data: {
        duration: formData.duration,
        Road: formData.Road,
        Construction: formData.Construction,
        Utilities: formData.Utilities,
        Consultancy: formData.Consultancy,
        new_end_date: upgradeDetails.new_end_date,
      }
    }).subscribe({
      next: (response: any) => {
        if (!response.success || !response.data) {
          this.loading = false;
          this.toastrService.error('Failed to create payment order');
          return;
        }
        const options = {
          key: response.data.key,
          amount: response.data.amount,
          currency: response.data.currency,
          name: 'SigmaTConflux Subscription',
          description: 'Subscription upgrade',
          order_id: response.data.orderId,
          handler: (paymentResponse: any) => {
            this.applyUpgrade(paymentResponse);
          },
          theme: { color: '#0058e9' },
          modal: {
            ondismiss: () => {
              this.loading = false;
            }
          }
        };
        const RazorpayCtor = (window as any).Razorpay;
        if (!RazorpayCtor) {
          this.loading = false;
          this.toastrService.error('Payment checkout failed to load');
          return;
        }
        new RazorpayCtor(options).open();
      },
      error: (error: any) => {
        this.loading = false;
        this.toastrService.error(error.error?.error || 'Failed to start payment');
      }
    });
  }

  private applyUpgrade(paymentResponse?: any): void {
    const formData = this.upgradeForm.value;
    this.apiService.processUpgradePayment(this.currentSubscription.id, {
      new_duration: formData.duration,
      upgrade_type: 'immediate',
      payment_gateway: 'razorpay',
      payment_id: paymentResponse?.razorpay_payment_id,
      razorpay_order_id: paymentResponse?.razorpay_order_id,
      razorpay_payment_id: paymentResponse?.razorpay_payment_id,
      razorpay_signature: paymentResponse?.razorpay_signature,
      Road: formData.Road,
      Construction: formData.Construction,
      Utilities: formData.Utilities,
      Consultancy: formData.Consultancy,
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.success) {
          this.toastrService.success('Subscription upgraded successfully');
          this.subscriptionAccessService.refreshSubscriptionAccess();
          this.dialogRef.close({ success: true });
        } else {
          this.toastrService.error(res.message || res.error || 'Upgrade failed');
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastrService.error(err.error?.error || err.error?.message || 'Upgrade failed');
      }
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  close(): void {
    this.dialogRef.close();
  }
}
